import type { typePlayerTabKey } from "../../routeState/playerSearch";
import {
  PLAYER_INFO_AGE_LABEL,
  PLAYER_INFO_COLLEGE_LABEL,
  PLAYER_INFO_EXPERIENCE_LABEL,
  PLAYER_INFO_HEIGHT_LABEL,
  PLAYER_INFO_NUMBER_LABEL,
  PLAYER_INFO_POSITION_LABEL,
  PLAYER_INFO_STATUS_LABEL,
  PLAYER_INFO_TEAM_LABEL,
  PLAYER_INFO_WEIGHT_LABEL,
  PLAYER_NO_DETAILS_LABEL,
  PLAYER_STATUS_ACTIVE_LABEL,
  PLAYER_TAB_GAME_LOG_LABEL,
  PLAYER_TAB_INFO_LABEL,
  PLAYER_TAB_STATS_LABEL,
} from "./playerProfileCopy";
import type {
  typePlayerProfileView,
  typePlayerRecord,
} from "./playerProfileReader";

export type typePlayerInjuryStatus =
  "out" | "doubtful" | "questionable" | "probable";

export const PLAYER_INJURY_STATUS_LABELS: Record<
  typePlayerInjuryStatus,
  string
> = {
  out: "Out",
  doubtful: "Doubtful",
  questionable: "Questionable",
  probable: "Probable",
};

/** The three tab labels, keyed by tab. */
export const PLAYER_TAB_LABELS: Record<typePlayerTabKey, string> = {
  info: PLAYER_TAB_INFO_LABEL,
  stats: PLAYER_TAB_STATS_LABEL,
  gameLog: PLAYER_TAB_GAME_LOG_LABEL,
};

/** A person's initials for the plate. First and last name, uppercased. Empty input yields "?". */
export function initialsFromPersonName(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part !== "");
  if (parts.length === 0) return "?";
  const first = parts[0];
  if (parts.length === 1) {
    return (first?.charAt(0) ?? "").toUpperCase();
  }
  const last = parts[parts.length - 1];
  const firstInitial = (first?.charAt(0) ?? "").toUpperCase();
  const lastInitial = (last?.charAt(0) ?? "").toUpperCase();
  return `${firstInitial}${lastInitial}`;
}

/** "12" and "#12" both render as "#12". Null or blank gives null. */
export function playerJerseyLabel(jerseyNumber: string | null): string | null {
  if (jerseyNumber === null) return null;
  const trimmed = jerseyNumber.trim();
  if (trimmed === "") return null;
  const withoutHash = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
  if (withoutHash === "") return null;
  return `#${withoutHash}`;
}

/** The one line under the player's name in the header. Never empty. */
export function playerMetaLine(input: {
  jerseyNumber: string | null;
  position: string | null;
}): string {
  const jerseyLabel = playerJerseyLabel(input.jerseyNumber);
  const position = input.position?.trim();
  const hasPosition = position !== undefined && position !== "";

  if (jerseyLabel !== null && hasPosition) {
    return `${jerseyLabel} · ${position}`;
  }
  if (jerseyLabel !== null) {
    return jerseyLabel;
  }
  if (hasPosition) {
    return position;
  }
  return PLAYER_NO_DETAILS_LABEL;
}

/** The parts of the header's meta line, "Team · Position · #23" (Scores UX
 *  spec v1.0 section 9), with every empty part dropped. */
export function playerMetaParts(input: {
  teamName: string | null;
  jerseyNumber: string | null;
  position: string | null;
}): { team: string | null; rest: string[] } {
  const team = input.teamName?.trim() ?? "";
  const position = input.position?.trim() ?? "";
  const jerseyLabel = playerJerseyLabel(input.jerseyNumber);
  return {
    team: team === "" ? null : team,
    rest: [position, jerseyLabel ?? ""].filter((part) => part !== ""),
  };
}

/** The header's injury line, or null when the caller sent no status. */
export function playerInjuryStatusLabel(
  status: typePlayerInjuryStatus | null,
): string | null {
  if (status === null) return null;
  return PLAYER_INJURY_STATUS_LABELS[status];
}

export type typePlayerInfoRowKey =
  | "team"
  | "position"
  | "number"
  | "status"
  | "height"
  | "weight"
  | "age"
  | "experience"
  | "college";

export type typePlayerInfoRow = {
  key: typePlayerInfoRowKey;
  label: string;
  value: string;
};

/** The Info rows in display order. A value that is null has no row. */
export function buildPlayerInfoRows(input: {
  /** The merged view (mergePlayerProfileView), so number, position and status already fall back to the record. */
  view: typePlayerProfileView;
  record: typePlayerRecord | null;
  hasInjuryReport: boolean;
}): typePlayerInfoRow[] {
  const { view, record, hasInjuryReport } = input;
  const rows: typePlayerInfoRow[] = [];
  const push = (
    key: typePlayerInfoRowKey,
    label: string,
    value: string | null | undefined,
  ): void => {
    if (value !== null && value !== undefined) rows.push({ key, label, value });
  };

  push("team", PLAYER_INFO_TEAM_LABEL, view.teamName);
  push("position", PLAYER_INFO_POSITION_LABEL, view.position);
  push(
    "number",
    PLAYER_INFO_NUMBER_LABEL,
    playerJerseyLabel(view.jerseyNumber),
  );
  const statusLabel = playerInjuryStatusLabel(view.injuryStatus);
  push(
    "status",
    PLAYER_INFO_STATUS_LABEL,
    statusLabel ??
      (record !== null && hasInjuryReport ? PLAYER_STATUS_ACTIVE_LABEL : null),
  );
  push("height", PLAYER_INFO_HEIGHT_LABEL, record?.heightLabel);
  push("weight", PLAYER_INFO_WEIGHT_LABEL, record?.weightLabel);
  push(
    "age",
    PLAYER_INFO_AGE_LABEL,
    typeof record?.age === "number" ? String(record.age) : null,
  );
  push("experience", PLAYER_INFO_EXPERIENCE_LABEL, record?.experienceLabel);
  push("college", PLAYER_INFO_COLLEGE_LABEL, record?.college);
  return rows;
}
